import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { submitVote } from "../api/voting";
import { fetchPhase, fetchCategoryBySlug, fetchAllRankings, type AwardPhase, type Category } from "../api/tcg";
import FinalistVoting from "../components/finalistVoting.component";
import Turnstile from "../components/turnstile.component";
import { useLanguage } from "../i18n/language.context";
import { apiErrorKey } from "../i18n/errorMessages";
import { fireConfetti } from "../utils/confetti.util";
import "./votingDetail.page.css";

type VotingStep = "intro" | "select" | "verify" | "done";

const GROUP_KEYS: Record<string, string> = {
    product: "ui.group.product.title",
    community: "ui.group.community.title",
    jury: "ui.group.jury.title",
    performance: "ui.group.performance.title",
};

function VotingDetailPage() {
    const { slug } = useParams<{ slug: string }>();
    const { t: tr, lang } = useLanguage();

    const [category, setCategory] = useState<Category | null>(null);
    const [categoryLoaded, setCategoryLoaded] = useState(false);
    const [phase, setPhase] = useState<AwardPhase | null>(null);

    const [step, setStep] = useState<VotingStep>("intro");
    const [entry, setEntry] = useState("");
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [captchaReset, setCaptchaReset] = useState(0);
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [winnersPublished, setWinnersPublished] = useState(false);

    const [votedName, setVotedName] = useState<string | null>(null);

    function updateEntry(value: string) {
        setEntry(value);
        if (submitError) setSubmitError(null);
    }

    function returnToEntryInput() {
        setEntry("");
        setSubmitError(null);
        setCaptchaToken(null);
        setCaptchaReset((n) => n + 1);
        setStep("select");
    }

    useEffect(() => {
        fetchPhase().then((p) => setPhase(p.phase)).catch(() => setPhase("IDLE"));
    }, []);

    useEffect(() => {
        if (!slug) {
            setCategoryLoaded(true);
            return;
        }

        fetchCategoryBySlug(slug, lang)
            .then((c) => setCategory(c))
            .catch(() => setCategory(null))
            .finally(() => setCategoryLoaded(true));
    }, [slug, lang]);

    useEffect(() => {
        if (!slug) return;

        fetchAllRankings()
            .then((all) => setWinnersPublished(all.some((r) => r.slug === slug)))
            .catch(() => setWinnersPublished(false));
    }, [slug]);

    async function handleSubmit() {
        const value = entry.trim();
        if (!slug || !value || !captchaToken) return;

        setSubmitting(true);
        setSubmitError(null);

        try {
            const result = await submitVote({ categorySlug: slug, entry: value, securityToken: captchaToken });
            if (result.ok) {
                setSubmitError(null);
                setStep("done");
                fireConfetti();
            } else {
                setSubmitError(tr(result.code ? apiErrorKey({ code: result.code }) : "ui.err.generic"));
                setCaptchaToken(null);
                setCaptchaReset((n) => n + 1);
            }
        } catch (err) {
            setSubmitError(tr(apiErrorKey(err)));
            setCaptchaToken(null);
            setCaptchaReset((n) => n + 1);
        } finally {
            setSubmitting(false);
        }
    }

    if (!categoryLoaded) {
        return (
            <div id="voting-detail-page" className="default-page max">
                <div className="voting-detail-container">
                    <p className="voting-detail-placeholder">{tr("ui.common.loading")}</p>
                </div>
            </div>
        );
    }

    if (!category) {
        return (
            <div id="voting-detail-page" className="default-page max">
                <div className="voting-detail-container">
                    <h1>{tr("ui.voting.notFoundTitle")}</h1>
                    <p className="voting-detail-subtitle">{tr("ui.voting.notFoundText")}</p>
                    <div className="voting-actions">
                        <Link to="/" className="voting-back-link">{tr("ui.voting.backHome")}</Link>
                    </div>
                </div>
            </div>
        );
    }

    const groupLabel = tr(GROUP_KEYS[category.group_key ?? "community"] ?? "ui.group.community.title");
    const votable = category.group_key === "product" || category.group_key === "community";
    const description = category.description ?? "";

    const backToHome = (
        <Link to="/#award-categories" className="voting-back-link">
            {tr("ui.nom.backAll")}
        </Link>
    );

    const viewWinners = winnersPublished && slug ? (
        <Link to={`/winners/${slug}`} className="voting-winners-link">
            {tr("ui.voting.viewWinners")}
        </Link>
    ) : null;

    const categoryActions = (
        <>
            {backToHome}
            {viewWinners}
        </>
    );

    return (
        <div id="voting-detail-page" className="default-page max">
            <div className="voting-detail-container">
                <div className="voting-detail-eyebrow">{groupLabel}</div>
                <h1 className="voting-detail-title">{category.name}</h1>

                {phase === null && (
                    <p className="voting-detail-placeholder">{tr("ui.common.loading")}</p>
                )}

                {phase !== null && !votable && (
                    <>
                        <section className="voting-detail-section">
                            {description && <p className="voting-detail-subtitle">{description}</p>}
                            <p className="voting-phase-hint">
                                {tr(winnersPublished ? "ui.voting.winnersAvailable" : "ui.voting.notVotable")}
                            </p>
                        </section>
                        <div className="voting-actions">{categoryActions}</div>
                    </>
                )}

                {phase === "IDLE" && votable && (
                    <>
                        <section className="voting-detail-section">
                            {description && <p className="voting-detail-subtitle">{description}</p>}
                            <p className="voting-phase-hint">
                                {tr(winnersPublished ? "ui.voting.winnersAvailable" : "ui.voting.idle")}
                            </p>
                        </section>
                        <div className="voting-actions">{categoryActions}</div>
                    </>
                )}

                {phase === "NOMINATION" && votable && step === "intro" && (
                    <>
                        {description && <p className="voting-detail-subtitle">{description}</p>}
                        <section className="voting-detail-section">
                            <h2>{tr("ui.nom.beforeTitle")}</h2>
                            <ul className="voting-detail-list">
                                <li>{tr("ui.nom.before1")}</li>
                                <li>{tr("ui.nom.before2")}</li>
                                <li>{tr("ui.nom.before3")}</li>
                            </ul>
                        </section>
                        <div className="voting-actions">
                            {categoryActions}
                            <button type="button" className="voting-primary-btn" onClick={() => setStep("select")}>
                                {tr("ui.common.continue")}
                            </button>
                        </div>
                    </>
                )}

                {phase === "NOMINATION" && votable && step === "select" && (
                    <>
                        <label className="voting-entry-label" htmlFor="voting-entry">
                            {tr("ui.nom.yourNominationFor")} {category.name}
                        </label>
                        <div className="voting-search">
                            <input
                                id="voting-entry"
                                type="text"
                                className="voting-search-input"
                                placeholder={tr("ui.nom.placeholder")}
                                value={entry}
                                maxLength={100}
                                onChange={(e) => updateEntry(e.target.value)}
                            />
                        </div>
                        <p className="voting-entry-hint">{tr("ui.nom.hint")}</p>
                        <div className="voting-actions">
                            <button type="button" className="voting-secondary-btn" onClick={() => setStep("intro")}>
                                {tr("ui.nom.backInfo")}
                            </button>
                            <button type="button" className="voting-primary-btn" disabled={!entry.trim()} onClick={() => setStep("verify")}>
                                {tr("ui.common.continue")}
                            </button>
                        </div>
                    </>
                )}

                {phase === "NOMINATION" && votable && step === "verify" && (
                    <>
                        <section className="voting-detail-section">
                            <h2>{tr("ui.nom.yourChoice")}</h2>
                            {entry.trim() ? (
                                <div className="voting-summary-name">{entry.trim()}</div>
                            ) : (
                                <p className="voting-detail-placeholder">{tr("ui.nom.noEntry")}</p>
                            )}
                        </section>
                        <section className="voting-detail-section">
                            <h2>{tr("ui.nom.security")}</h2>
                            <Turnstile
                                onToken={setCaptchaToken}
                                onExpire={() => setCaptchaToken(null)}
                                resetSignal={captchaReset}
                            />
                        </section>
                        {submitError && <p className="voting-error">{submitError}</p>}
                        <div className="voting-actions">
                            <button type="button" className="voting-secondary-btn" onClick={returnToEntryInput} disabled={submitting}>
                                {tr("ui.common.back")}
                            </button>
                            <button type="button" className="voting-primary-btn" disabled={!entry.trim() || !captchaToken || submitting} onClick={handleSubmit}>
                                {submitting ? tr("ui.nom.submitting") : tr("ui.nom.submit")}
                            </button>
                        </div>
                    </>
                )}

                {phase === "NOMINATION" && votable && step === "done" && (
                    <section className="voting-detail-section">
                        <h2>{tr("ui.nom.doneTitle")}</h2>
                        <p className="voting-detail-placeholder">{tr("ui.nom.doneText")}</p>
                        <div className="voting-actions">
                            {categoryActions}
                            <button
                                type="button"
                                className="voting-primary-btn"
                                onClick={() => {
                                    setEntry("");
                                    setCaptchaToken(null);
                                    setSubmitError(null);
                                    setStep("select");
                                }}
                            >
                                {tr("ui.nom.another")}
                            </button>
                        </div>
                    </section>
                )}

                {phase === "VOTING" && votable && votedName === null && (
                    <>
                        {description && <p className="voting-detail-subtitle">{description}</p>}
                        <section className="voting-detail-section">
                            <h2>{tr("ui.voting.voteFavorite")}</h2>
                            {slug && (
                                <FinalistVoting
                                    categorySlug={slug}
                                    onVoted={(name) => {
                                        setVotedName(name);
                                        fireConfetti();
                                        window.scrollTo({ top: 0, behavior: "smooth" });
                                    }}
                                />
                            )}
                        </section>
                        <div className="voting-actions">{categoryActions}</div>
                    </>
                )}

                {phase === "VOTING" && votable && votedName !== null && (
                    <section className="voting-detail-section">
                        <h2>{tr("ui.vote.doneTitle")}</h2>
                        <p className="voting-detail-placeholder">{tr("ui.vote.doneText")}</p>
                        {votedName && <div className="voting-summary-name">{votedName}</div>}
                        <div className="voting-actions">
                            {categoryActions}
                            <button
                                type="button"
                                className="voting-secondary-btn"
                                onClick={() => setVotedName(null)}
                            >
                                {tr("ui.vote.change")}
                            </button>
                        </div>
                    </section>
                )}
            </div>
        </div>
    );
}

export default VotingDetailPage;